/**
 * Note Section Item
 *
 * Displays a single note section with:
 * - Section name with purple underline styling
 * - Editable markdown content using EditorView
 * - Visual feedback during AI streaming updates
 * - Highlight effect for affected sections
 * - Trouble words misspelling detection and highlighting
 * - Bracket highlight plugin for template placeholders
 * - Analytics integration for user behavior tracking
 * - Compliance rule indicators from JotCoach encounter session
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { openAuditForSection } from '@features/jotaudit';
import {
  AttestationEditIndicator,
  AttestationEditPopover,
  ComplianceRuleIndicator,
  ComplianceRulePopover,
} from '@features/jotcoach';
import { useJotCoachRules } from '@features/jotcoach/store/hooks';
import type { NoteSection } from '@features/notes';
import { useTroubleWords } from '@features/user';
import CheckIcon from '@mui/icons-material/Check';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UndoIcon from '@mui/icons-material/Undo';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useModal } from '@store/hooks/useModal';
import { useSnackbar } from 'notistack';

import { BracketReplacementPopover } from '../../../components/BlockNote/BracketReplacementPopover';
import { EditorView } from '../../../components/BlockNote/EditorView';
import type { BracketMatch } from '../../../components/BlockNoteBracketHighlightPlugin';
import { MODAL_TYPES } from '../../../components/modals/constants';
import type { AnyBlockNoteEditor } from '../../../utils/blocknote/types';
import { useReportSectionPlaceholders } from '../context/notePlaceholders';
import { useNoteSectionCopy } from '../hooks/useNoteSectionCopy';
import { useSectionRevert } from '../hooks/useSectionRevert';
import {
  clearAffectedSection,
  clearFocusedSection,
  clearSectionSnapshot,
  setChatVisibility,
  setDirtySection,
  setFocusedSection,
  toggleCopySectionSelection,
} from '../store/chatSlice';
import { selectSelectedSectionIdsForCopy } from '../store/selectors';
import type { EditPreview } from '../types';

import { SectionDiffModal } from './SectionDiffModal';
import { SectionFormattingToolbar } from './SectionFormattingToolbar';
import { SectionSelector } from './SectionSelector';

interface NoteSectionItemProps {
  /**
   * Section data from note
   */
  section: NoteSection;

  /**
   * Section index for updates
   */
  index: number;

  /**
   * Whether this section is affected by pending AI changes
   */
  isAffected?: boolean;

  /**
   * Whether AI is currently streaming changes to this section
   */
  isStreaming?: boolean;

  /**
   * Callback when section content changes
   * Returns a Promise that resolves when the save completes
   */
  onChange: (index: number, editedText: string) => Promise<void>;

  /**
   * Pending AI-generated content (for streaming preview)
   */
  pendingContent?: string;

  /**
   * Array of edit previews with diff data (for affected sections)
   */
  edits?: EditPreview[];

  /**
   * Encounter ID for analytics metadata
   */
  encounterId?: string;

  /**
   * Whether section should be editable
   */
  editable?: boolean;

  /**
   * Callback when user approves the AI suggestion (dismisses affected state)
   */
  onApprove?: () => void;

  /**
   * Callback when user reverts the AI suggestion
   */
  onRevert?: () => void;

  /**
   * Previous content before AI edit (for diff modal and revert)
   */
  previousContent?: string;

  /**
   * Compliance rule IDs for this section from JotCoach encounter session
   * Used to display compliance rule indicators next to section heading
   */
  complianceRuleIds?: string[];

  /**
   * Attestation edits for this section from JotCoach encounter session
   */
  attestationEdits?: Array<{
    note_section: string;
    rationale: string;
    rule: string;
    text: string;
    title: string;
  }>;

  /**
   * Callback when a save operation starts (for navigation guard)
   * Receives the section ID and save promise to track completion
   */
  onSaveStart?: (sectionId: string, savePromise: Promise<void>) => void;

  /**
   * Callback when section content is modified (marks as dirty)
   */
  onMarkDirty?: (sectionId: string) => void;

  /**
   * Number of audit issues (warnings + non-compliant) for this section
   * Computed from RTK Query audit data and passed down
   */
  sectionIssueCount?: number;

  /**
   * Optional callback when BlockNote editor is ready or unmounted
   * Allows parent to maintain editor registry for copy operations
   * Receives editor instance on mount, null on unmount
   */
  onEditorReady?: (editor: AnyBlockNoteEditor | null) => void;
}

// Override to disable diff view
const ENABLE_DIFF_OVERRIDE = false;

export const NoteSectionItem: React.FC<NoteSectionItemProps> = React.memo(
  ({
    section,
    index,
    isAffected = false,
    isStreaming = false,
    onChange,
    pendingContent,
    edits,
    encounterId,
    editable = true,
    onApprove,
    onRevert,
    previousContent,
    complianceRuleIds,
    attestationEdits,
    onSaveStart,
    onMarkDirty,
    sectionIssueCount = 0,
    onEditorReady,
  }) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { noteId } = useParams<{ noteId?: string }>();
    const { enqueueSnackbar } = useSnackbar();
    const selectedSectionIds = useAppSelector(selectSelectedSectionIdsForCopy);
    const { noteSection, noteAudit } = useEventTracking();

    // Section revert hook
    const { revertSection } = useSectionRevert({ noteId: noteId || '' });

    // Trouble Words System
    const troubleWords = useTroubleWords();
    const { open: openModal } = useModal();

    // Bracket Highlight State
    const [bracketMatch, setBracketMatch] = useState<BracketMatch | null>(null);
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

    // Diff Modal State
    const [diffModalOpen, setDiffModalOpen] = useState(false);

    // Hover state for formatting toolbar visibility
    const [isHovered, setIsHovered] = useState(false);

    // Optimistic content when applying attestation edits
    const [optimisticContent, setOptimisticContent] = useState<string | undefined>(undefined);

    // Focus state for formatting toolbar visibility (stays visible while editing)
    const [isFocused, setIsFocused] = useState(false);

    // Editor Reference for programmatic control
    const editorRef = useRef<AnyBlockNoteEditor | null>(null);

    // Section container ref for click-outside detection
    const sectionRef = useRef<HTMLDivElement | null>(null);

    // Analytics: Document version tracking
    const docVersionRef = useRef<number>(0);
    const lastEditedTextRef = useRef<string>(section.edited_text || section.text);

    // Track current editor content for blur-only save
    const currentMarkdownRef = useRef<string>(section.edited_text || section.text);

    // Track if user has made edits in current editing session
    // Prevents external displayContent updates from overwriting user's changes
    const hasUserEditedRef = useRef<boolean>(false);

    // Track if user has focused this editor (to distinguish user edits from BlockNote normalization)
    const hasUserFocusedRef = useRef<boolean>(false);

    // ============================================================================
    // COMPLIANCE RULE INDICATOR STATE
    // ============================================================================
    // State and refs for rendering compliance rule indicators next to section heading
    // Pattern borrowed from: src/web-app/src/features/jotdocs/components/jot-doc-section.tsx

    // Compliance rule indicator hover state
    const [hoveredRuleId, setHoveredRuleId] = useState<string | null>(null);
    const indicatorRefs = useRef<{ [key: string]: HTMLElement | null }>({});
    const closeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    // Fetch all compliance rules from API
    const { rules: allRules } = useJotCoachRules();

    // Filter rules to only those matching this section's complianceRuleIds
    const complianceRules = useMemo(() => {
      if (
        !complianceRuleIds ||
        complianceRuleIds.length === 0 ||
        !allRules ||
        allRules.length === 0
      ) {
        return [];
      }
      const ruleIdSet = new Set(complianceRuleIds);
      return allRules.filter(rule => ruleIdSet.has(rule.id));
    }, [complianceRuleIds, allRules]);

    // Compliance rule indicator hover handlers
    const cancelCloseTimeout = useCallback(() => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = undefined;
      }
    }, []);

    const handleIndicatorEnter = useCallback(
      (ruleId: string) => {
        cancelCloseTimeout();
        setHoveredRuleId(ruleId);
      },
      [cancelCloseTimeout],
    );

    const handleIndicatorLeave = useCallback(() => {
      cancelCloseTimeout();
      closeTimeoutRef.current = setTimeout(() => {
        setHoveredRuleId(null);
      }, 150);
    }, [cancelCloseTimeout]);

    const handlePopoverMouseEnter = useCallback(() => {
      cancelCloseTimeout();
    }, [cancelCloseTimeout]);

    const handlePopoverMouseLeave = useCallback(() => {
      cancelCloseTimeout();
      closeTimeoutRef.current = setTimeout(() => {
        setHoveredRuleId(null);
      }, 150);
    }, [cancelCloseTimeout]);

    const handlePopoverClose = useCallback(() => {
      cancelCloseTimeout();
      setHoveredRuleId(null);
    }, [cancelCloseTimeout]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        cancelCloseTimeout();
      };
    }, [cancelCloseTimeout]);

    // Get hovered rule and anchor element for popover
    const hoveredRule = hoveredRuleId
      ? complianceRules.find(rule => rule.id === hoveredRuleId)
      : null;
    const anchorEl = hoveredRuleId ? indicatorRefs.current[hoveredRuleId] : null;

    // ============================================================================
    // END COMPLIANCE RULE INDICATOR STATE
    // ============================================================================

    // ============================================================================
    // ATTESTATION EDIT INDICATOR STATE
    // ============================================================================
    const buildAttestationKey = useCallback(
      (edit: { rule: string; title: string }, index: number) =>
        `${section.section_id}-${edit.rule || 'rule'}-${edit.title || index}`,
      [section.section_id],
    );

    const [dismissedAttestationEditIds, setDismissedAttestationEditIds] = useState<Set<string>>(
      new Set(),
    );
    const [hoveredAttestationEditIndex, setHoveredAttestationEditIndex] = useState<number | null>(
      null,
    );
    const attestationIndicatorRefs = useRef<{ [key: number]: HTMLElement | null }>({});
    const attestationCloseTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const viewedAttestationIdsRef = useRef<Set<string>>(new Set());

    const visibleAttestationEdits = useMemo(() => {
      if (!attestationEdits || attestationEdits.length === 0) return [];
      return attestationEdits.filter(
        (edit, idx) => !dismissedAttestationEditIds.has(buildAttestationKey(edit, idx)),
      );
    }, [attestationEdits, dismissedAttestationEditIds, buildAttestationKey]);

    const cancelAttestationCloseTimeout = useCallback(() => {
      if (attestationCloseTimeoutRef.current) {
        clearTimeout(attestationCloseTimeoutRef.current);
        attestationCloseTimeoutRef.current = undefined;
      }
    }, []);

    const handleAttestationIndicatorEnter = useCallback(
      (index: number) => {
        cancelAttestationCloseTimeout();
        setHoveredAttestationEditIndex(index);
      },
      [cancelAttestationCloseTimeout],
    );

    const handleAttestationIndicatorLeave = useCallback(() => {
      cancelAttestationCloseTimeout();
      attestationCloseTimeoutRef.current = setTimeout(() => {
        setHoveredAttestationEditIndex(null);
      }, 150);
    }, [cancelAttestationCloseTimeout]);

    const handleAttestationPopoverMouseEnter = useCallback(() => {
      cancelAttestationCloseTimeout();
    }, [cancelAttestationCloseTimeout]);

    const handleAttestationPopoverMouseLeave = useCallback(() => {
      cancelAttestationCloseTimeout();
      attestationCloseTimeoutRef.current = setTimeout(() => {
        setHoveredAttestationEditIndex(null);
      }, 150);
    }, [cancelAttestationCloseTimeout]);

    const handleAttestationPopoverClose = useCallback(() => {
      cancelAttestationCloseTimeout();
      setHoveredAttestationEditIndex(null);
    }, [cancelAttestationCloseTimeout]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        cancelAttestationCloseTimeout();
      };
    }, [cancelAttestationCloseTimeout]);

    const hoveredAttestationEdit =
      hoveredAttestationEditIndex !== null
        ? visibleAttestationEdits[hoveredAttestationEditIndex]
        : null;

    const attestationAnchorEl =
      hoveredAttestationEditIndex !== null
        ? attestationIndicatorRefs.current[hoveredAttestationEditIndex]
        : null;

    const getCurrentSectionContent = useCallback(async () => {
      if (editorRef.current) {
        try {
          return await editorRef.current.blocksToMarkdownLossy(editorRef.current.document);
        } catch (err) {
          console.error('Failed to read editor content for attestation append:', err);
        }
      }
      return currentMarkdownRef.current || section.edited_text || section.text || '';
    }, [section.edited_text, section.text]);

    const handleAcceptAttestationEdit = useCallback(
      async (editIndex: number) => {
        const edit = visibleAttestationEdits[editIndex];
        if (!edit) return;

        try {
          const currentContent = await getCurrentSectionContent();
          const newContent = currentContent ? `${currentContent}\n${edit.text}` : edit.text;

          // Optimistically update local content so UI reflects immediately
          setOptimisticContent(newContent);
          currentMarkdownRef.current = newContent;
          hasUserEditedRef.current = true;
          onMarkDirty?.(section.section_id);

          const savePromise = (async () => {
            await onChange(index, newContent);
          })();

          onSaveStart?.(section.section_id, savePromise);
          await savePromise;

          setDismissedAttestationEditIds(prev => {
            const next = new Set(prev);
            next.add(buildAttestationKey(edit, editIndex));
            return next;
          });
          setHoveredAttestationEditIndex(null);

          noteSection.attestationAccepted({
            section_id: section.section_id,
            note_id: noteId,
            encounter_id: encounterId,
            rule_id: edit.rule,
            title: edit.title,
            source: 'popover_accept',
          });
        } catch (error) {
          console.error('Failed to apply attestation edit:', error);
          enqueueSnackbar('Failed to apply attestation edit. Please try again.', {
            variant: 'error',
          });
        }
      },
      [
        visibleAttestationEdits,
        getCurrentSectionContent,
        onChange,
        index,
        onSaveStart,
        section.section_id,
        buildAttestationKey,
        enqueueSnackbar,
        onMarkDirty,
      ],
    );

    const handleRejectAttestationEdit = useCallback(
      (editIndex: number) => {
        const edit = visibleAttestationEdits[editIndex];
        if (!edit) return;
        setDismissedAttestationEditIds(prev => {
          const next = new Set(prev);
          next.add(buildAttestationKey(edit, editIndex));
          return next;
        });
        setHoveredAttestationEditIndex(null);

        noteSection.attestationRejected({
          section_id: section.section_id,
          note_id: noteId,
          encounter_id: encounterId,
          rule_id: edit.rule,
          title: edit.title,
          source: 'popover_reject',
        });
      },
      [
        visibleAttestationEdits,
        buildAttestationKey,
        noteSection,
        section.section_id,
        noteId,
        encounterId,
      ],
    );

    // Track attestation viewed when popover opens (once per edit)
    useEffect(() => {
      if (!hoveredAttestationEdit || hoveredAttestationEditIndex === null) {
        return;
      }
      const key = buildAttestationKey(hoveredAttestationEdit, hoveredAttestationEditIndex);
      if (viewedAttestationIdsRef.current.has(key)) {
        return;
      }
      viewedAttestationIdsRef.current.add(key);
      noteSection.attestationViewed({
        section_id: section.section_id,
        note_id: noteId,
        encounter_id: encounterId,
        rule_id: hoveredAttestationEdit.rule,
        title: hoveredAttestationEdit.title,
        source: 'popover_hover',
      });
    }, [
      hoveredAttestationEdit,
      hoveredAttestationEditIndex,
      buildAttestationKey,
      noteSection,
      section.section_id,
      noteId,
      encounterId,
    ]);

    // ============================================================================
    // END ATTESTATION EDIT INDICATOR STATE
    // ============================================================================

    // Trouble Words: Compute per-section misspellings
    const sectionMisspellings = useMemo(() => {
      const sectionText = (section.edited_text || section.text || '').toLowerCase();
      const misspellings: string[] = [];

      if (!troubleWords || troubleWords.length === 0) {
        return misspellings;
      }

      troubleWords.forEach(troubleWord => {
        // Check each misspelling against section text
        troubleWord.misspellings.forEach(misspelling => {
          const regex = new RegExp(`\\b${misspelling}\\b`, 'gi');
          if (regex.test(sectionText)) {
            misspellings.push(misspelling);
          }
        });

        // Also check if correct spelling exists in text (for highlighting)
        const correctRegex = new RegExp(`\\b${troubleWord.correct_spelling}\\b`, 'gi');
        if (correctRegex.test(sectionText)) {
          misspellings.push(troubleWord.correct_spelling);
        }
      });

      return misspellings;
    }, [section.edited_text, section.text, troubleWords]);

    // Analytics: Track document version changes
    useEffect(() => {
      const currentText = section.edited_text || section.text;
      if (lastEditedTextRef.current !== currentText) {
        docVersionRef.current += 1;
        lastEditedTextRef.current = currentText;
      }
    }, [section.edited_text, section.text]);

    // Analytics: Document version getter
    const getDocVersion = useCallback(() => docVersionRef.current.toString(), []);

    // Trouble Words: Handle word click to open FixSpellingModal
    const handleWordClick = useCallback(
      (word: string) => {
        if (!noteId) return;

        // Find the trouble word entry for correct spelling lookup
        const troubleWordEntry = troubleWords?.find(
          entry =>
            entry.correct_spelling.toLowerCase() === word.toLowerCase() ||
            entry.misspellings.some(
              misspelling => misspelling.toLowerCase() === word.toLowerCase(),
            ),
        );

        // Determine if clicked word is a misspelling or correct spelling
        const isMisspelling = troubleWordEntry?.misspellings.some(
          misspelling => misspelling.toLowerCase() === word.toLowerCase(),
        );

        // Open modal with appropriate prefills
        openModal(MODAL_TYPES.FIX_SPELLING, {
          sections: [section], // Pass current section for context
          prefillFind: word,
          prefillReplace: isMisspelling ? troubleWordEntry?.correct_spelling : undefined,
          analyticsMetadata: {
            note_id: noteId,
            source: 'misspelling',
            prefilled_term: true,
            encounter_id: encounterId,
          },
        });
      },
      [noteId, troubleWords, section, encounterId, openModal],
    );

    // Bracket Highlight: Handle bracket click
    const handleBracketClick = useCallback((match: BracketMatch, event: MouseEvent) => {
      setBracketMatch(match);
      setPopoverAnchor(event.target as HTMLElement);
    }, []);

    // Bracket Highlight: Handle bracket replacement
    const handleBracketReplace = useCallback(
      async (replacementText: string) => {
        if (!editorRef.current || !bracketMatch) return;

        try {
          // Access underlying TipTap/ProseMirror instance for direct text replacement
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const tiptapEditor = (editorRef.current as any)._tiptapEditor;
          if (!tiptapEditor || !tiptapEditor.view) {
            console.warn('BlockNote editor TipTap instance not available');
            return;
          }

          const { state, dispatch } = tiptapEditor.view;
          const { tr } = state;

          // Replace bracket text with user input (no brackets)
          const trimmedReplacement = replacementText.trim();
          tr.replaceWith(bracketMatch.from, bracketMatch.to, state.schema.text(trimmedReplacement));
          dispatch(tr);

          // Get updated markdown and update ref (will save on blur)
          const updatedMarkdown = await editorRef.current.blocksToMarkdownLossy(
            editorRef.current.document,
          );
          currentMarkdownRef.current = updatedMarkdown;

          // Close popover
          setPopoverAnchor(null);
          setBracketMatch(null);
        } catch (error) {
          console.error('Error replacing bracket text:', error);
          enqueueSnackbar('Failed to replace bracket text', { variant: 'error' });
        }
      },
      [bracketMatch, enqueueSnackbar],
    );

    // Bracket Highlight: Close popover
    const handleBracketPopoverClose = useCallback(() => {
      setPopoverAnchor(null);
      setBracketMatch(null);
    }, []);

    // Editor Ready: Store reference for programmatic control
    const handleEditorReady = useCallback(
      (editor: AnyBlockNoteEditor) => {
        editorRef.current = editor;
        // Notify parent of editor instance for copy operations
        onEditorReady?.(editor);
      },
      [onEditorReady],
    );

    // Cleanup: Notify parent when component unmounts to remove editor from registry
    useEffect(() => {
      return () => {
        onEditorReady?.(null);
      };
    }, [onEditorReady]);

    // Note-level placeholder aggregation: report this section's outstanding
    // bracket placeholders up so the note can count them, jump between them,
    // and soft-gate signing until they are filled.
    const reportSectionPlaceholders = useReportSectionPlaceholders();
    const handleBracketsChanged = useCallback(
      (matches: BracketMatch[]) => {
        reportSectionPlaceholders(section.section_id, matches);
      },
      [reportSectionPlaceholders, section.section_id],
    );
    useEffect(() => {
      return () => {
        // Drop this section's contribution when it unmounts.
        reportSectionPlaceholders(section.section_id, []);
      };
    }, [reportSectionPlaceholders, section.section_id]);

    // Find matching edit with diff data for this section
    const edit = useMemo(
      () => edits?.find(e => e.section_id === section.section_id),
      [edits, section.section_id],
    );

    // Copy functionality for individual section
    const { copySection, copyState } = useNoteSectionCopy();

    // Check if this section is selected for copy
    const isSelected = selectedSectionIds.includes(section.section_id);

    // Determine content to display
    // Show pending AI content whenever it exists (during streaming AND approval phase)
    // Falls back to section content when no pending updates
    const displayContent = useMemo(() => {
      if (optimisticContent !== undefined) {
        return optimisticContent;
      }
      if (pendingContent !== undefined) {
        return pendingContent;
      }
      return section.edited_text || section.text;
    }, [optimisticContent, pendingContent, section.edited_text, section.text]);

    // Update current markdown ref when display content changes (from props)
    // Only sync if user hasn't started editing to prevent overwriting user's changes
    useEffect(() => {
      if (!hasUserEditedRef.current) {
        currentMarkdownRef.current = displayContent;
      }
    }, [displayContent]);

    // Reset edit tracking when section content changes from external source (e.g., after save)
    useEffect(() => {
      hasUserEditedRef.current = false;
      hasUserFocusedRef.current = false;
      setOptimisticContent(undefined);
      // Note: Dirty section is cleared in handleBlur after successful save.
      // We don't clear it here to avoid race conditions with user edits.
    }, [section.edited_text, section.text]);

    // Determine if section is loading (empty with no error or streaming)
    const isLoading = useMemo(() => {
      const hasContent = section.text || section.edited_text || pendingContent;
      const hasError = !!section.error;
      return !hasContent && !hasError && !isStreaming;
    }, [section.text, section.edited_text, section.error, pendingContent, isStreaming]);

    // Update ref on every change (no save triggered - save only on blur)
    // NOTE: Markdown conversion is already debounced in EditorView (300ms),
    // so this callback is only called after typing settles
    const handleMarkdownChange = useCallback(
      (markdown: string) => {
        // Guard: Skip initial sync from EditorView initialization
        if (markdown === displayContent) {
          return;
        }

        // Guard: Skip if user hasn't focused this editor yet
        // This prevents BlockNote's initial normalization from marking sections dirty
        if (!hasUserFocusedRef.current) {
          return;
        }

        // Only mark dirty if user has already started editing (not initial BlockNote normalization)
        // BlockNote normalizes markdown on load which triggers onChange even without user interaction
        const wasAlreadyEditing = hasUserEditedRef.current;

        // Mark that user has made edits to prevent displayContent sync from overwriting
        hasUserEditedRef.current = true;
        currentMarkdownRef.current = markdown;

        // Track dirty content in Redux for copy functionality (only after user has focused)
        dispatch(setDirtySection({ sectionId: section.section_id, content: markdown }));

        // If user manually edits an AI-affected section, clear the snapshot so the
        // audit card's undo is disabled — the user's edits take precedence.
        if (isAffected && wasAlreadyEditing) {
          dispatch(clearSectionSnapshot(section.section_id));
          dispatch(clearAffectedSection(section.section_id));
        }

        // Only mark navigation-dirty if user was already editing
        // This prevents initial BlockNote normalization from marking all sections dirty
        if (wasAlreadyEditing) {
          onMarkDirty?.(section.section_id);
        }
      },
      [displayContent, onMarkDirty, section.section_id, dispatch, isAffected],
    );

    // Save only on blur if content changed
    const handleBlur = useCallback(() => {
      // Clear focus state for toolbar visibility
      setIsFocused(false);

      const currentContent = currentMarkdownRef.current;
      const originalContent = section.edited_text || section.text;

      if (currentContent !== originalContent) {
        // Create save promise and register it with guard
        const savePromise = (async () => {
          try {
            await onChange(index, currentContent);
            // NOTE: Don't clear dirty section here!
            // The cache won't have the updated content (due to _skipInvalidation: true),
            // so we keep the dirty content for copy functionality.
            // Dirty sections are cleared when navigating away from the note.
          } catch (error) {
            console.error('Failed to save section on blur:', error);
            enqueueSnackbar('Failed to save section changes. Please try again.', {
              variant: 'error',
            });
            // Re-throw to ensure promise is marked as rejected
            throw error;
          }
        })();

        // Register save promise with guard (will auto-track completion and clear dirty state)
        onSaveStart?.(section.section_id, savePromise);
      }
    }, [
      onChange,
      index,
      section.section_id,
      section.edited_text,
      section.text,
      enqueueSnackbar,
      onSaveStart,
    ]);

    // Handle focus to show formatting toolbar and update sidebar banner
    const handleFocus = useCallback(() => {
      setIsFocused(true);
      // Mark that user has focused this editor (enables dirty tracking)
      hasUserFocusedRef.current = true;
      // Update sidebar banner with focused section info
      dispatch(setFocusedSection({ id: section.section_id, name: section.name }));
    }, [dispatch, section.section_id, section.name]);

    const handleSelectorToggle = useCallback(
      (sectionId: string) => {
        // Track selector click before toggling (action is the new state after click)
        const isSelected = selectedSectionIds.includes(sectionId);
        const action = isSelected ? 'deselect' : 'select';
        noteSection.selectorClicked({
          section_id: sectionId,
          section_name: section.name,
          action,
          source: 'section',
          note_id: noteId,
          encounter_id: encounterId,
        });

        dispatch(toggleCopySectionSelection(sectionId));
      },
      [dispatch, selectedSectionIds, section.name, noteId, encounterId, noteSection],
    );

    const handleCopySection = useCallback(() => {
      // Clear affected state when copying (implicit acceptance)
      if (isAffected) {
        dispatch(clearAffectedSection(section.section_id));
        dispatch(clearSectionSnapshot(section.section_id));
        if (onApprove) {
          onApprove();
        }
      }
      // currentMarkdownRef always has the correct content:
      // - Synced from displayContent (which includes pendingContent) when user hasn't edited
      // - Updated directly when user edits
      // Pass editor instance for better copy fidelity
      copySection(
        section,
        noteId,
        encounterId,
        currentMarkdownRef.current,
        editorRef.current ?? undefined,
      );
    }, [copySection, section, noteId, encounterId, isAffected, dispatch, onApprove]);

    // Handle approve (dismiss affected state)
    const handleApprove = useCallback(() => {
      dispatch(clearAffectedSection(section.section_id));
      dispatch(clearSectionSnapshot(section.section_id));
      if (onApprove) {
        onApprove();
      }
    }, [dispatch, section.section_id, onApprove]);

    // Handle revert (revert to previous content)
    const handleRevert = useCallback(async () => {
      try {
        await revertSection(section.section_id, index);
        if (onRevert) {
          onRevert();
        }
      } catch (error) {
        console.error('Failed to revert section:', error);
        enqueueSnackbar('Failed to revert section. Please try again.', {
          variant: 'error',
        });
      }
    }, [revertSection, section.section_id, index, onRevert, enqueueSnackbar]);

    // Handle diff modal open
    const handleDiffClick = useCallback(() => {
      setDiffModalOpen(true);
    }, []);

    // Handle diff modal close
    const handleDiffClose = useCallback(() => {
      setDiffModalOpen(false);
    }, []);

    // Handle approve from diff modal
    const handleDiffApprove = useCallback(() => {
      dispatch(clearAffectedSection(section.section_id));
      dispatch(clearSectionSnapshot(section.section_id));
      if (onApprove) {
        onApprove();
      }
      setDiffModalOpen(false);
    }, [dispatch, section.section_id, onApprove]);

    // Handle reject from diff modal
    const handleDiffReject = useCallback(async () => {
      try {
        await revertSection(section.section_id, index);
        if (onRevert) {
          onRevert();
        }
        setDiffModalOpen(false);
      } catch (error) {
        console.error('Failed to revert section:', error);
        enqueueSnackbar('Failed to revert section. Please try again.', {
          variant: 'error',
        });
      }
    }, [revertSection, section.section_id, index, onRevert, enqueueSnackbar]);

    // Hover handlers for formatting toolbar
    const handleMouseEnter = useCallback(() => {
      if (editable && !isStreaming) {
        setIsHovered(true);
      }
    }, [editable, isStreaming]);

    const handleMouseLeave = useCallback(() => {
      setIsHovered(false);
    }, []);

    // Click handler for the editor wrapper to ensure focus state is captured
    const handleEditorWrapperClick = useCallback(() => {
      if (editable && !isStreaming) {
        setIsFocused(true);
        dispatch(setFocusedSection({ id: section.section_id, name: section.name }));
      }
    }, [editable, isStreaming, dispatch, section.section_id, section.name]);

    // Handle click outside to clear focus state
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        // Only clear focus if this section is currently focused
        if (!isFocused) return;

        const target = event.target as Node;

        // Check if click is outside the section element
        if (!sectionRef.current || sectionRef.current.contains(target)) {
          return;
        }

        // Don't clear focus if click is inside the chat sidebar
        const chatSidebar = document.querySelector('[data-chat-sidebar-container]');
        if (chatSidebar && chatSidebar.contains(target)) {
          return;
        }

        // Click is outside both section and chat - clear focus
        setIsFocused(false);
        dispatch(clearFocusedSection());
      };

      // Always add listener, but handler checks isFocused state
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isFocused, dispatch]);

    return (
      <Box
        ref={sectionRef}
        data-section-id={section.section_id}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'relative',
          mb: 3,
          maxWidth: '100%',
          // Streaming indicator (only during active streaming)
          ...((isStreaming || isAffected) && {
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 60,
              left: '-32px',
              bottom: 28,
              width: 3,
              background: isStreaming
                ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                : theme.palette.primary.main,
              animation: isStreaming ? 'streamingPulse 2s ease-in-out infinite' : undefined,
            },
            '@keyframes streamingPulse': {
              '0%, 100%': { opacity: 0.5 },
              '50%': { opacity: 1 },
            },
          }),
        }}
      >
        {/* Attestation edits gutter indicators */}
        {visibleAttestationEdits.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: '-56px',
              top: 0,
              bottom: 28,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: 1,
              zIndex: 1,
            }}
          >
            {visibleAttestationEdits.map((edit, idx) => (
              <Box
                key={buildAttestationKey(edit, idx)}
                ref={el => {
                  attestationIndicatorRefs.current[idx] = el as HTMLElement | null;
                }}
              >
                <AttestationEditIndicator
                  isHovered={hoveredAttestationEditIndex === idx}
                  onMouseEnter={() => handleAttestationIndicatorEnter(idx)}
                  onMouseLeave={handleAttestationIndicatorLeave}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Quick Action Buttons - Approve/Revert/Diff (only when section is affected) */}
        {isAffected && (
          <Box
            sx={{
              position: 'absolute',
              left: '-80px',
              top: '60px',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              zIndex: 1,
            }}
          >
            <Tooltip title="Approve changes" placement="right">
              <IconButton
                size="small"
                onClick={handleApprove}
                sx={{
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    borderColor: 'success.main',
                    color: 'success.main',
                  },
                }}
                aria-label="Approve changes"
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Revert to original" placement="right">
              <IconButton
                size="small"
                onClick={handleRevert}
                sx={{
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    borderColor: 'error.main',
                    color: 'error.main',
                  },
                }}
                aria-label="Revert to original"
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View changes" placement="right">
              <IconButton
                size="small"
                onClick={handleDiffClick}
                sx={{
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                  },
                }}
                aria-label="View changes"
              >
                <CompareArrowsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {/* Section Name with Selector and Purple Underline */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            pb: 1.5,
            borderBottom: `1px solid #E6E1FF`,
            justifyContent: 'space-between',
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            flex: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              minWidth: 0,
              overflow: 'hidden',
              flex: 1,
            }}
          >
            <SectionSelector
              sectionId={section.section_id}
              isSelected={isSelected}
              onToggle={handleSelectorToggle}
            />
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontSize: { xs: '18px', sm: '20px', md: '24px' },
                fontWeight: 700,
                color: theme.palette.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {section.name}
            </Typography>
            {/* Audit Issue Badge - clickable to open audit sidebar */}
            {sectionIssueCount > 0 && (
              <Tooltip title="View audit issues for this section">
                <Box
                  onClick={e => {
                    e.stopPropagation();
                    // Track section audit badge click
                    noteAudit.sectionBadgeClicked({
                      note_id: noteId,
                      section_id: section.section_id,
                      section_name: section.name,
                      encounter_id: encounterId,
                      issue_count: sectionIssueCount,
                    });
                    // Track sidebar opened from section badge
                    noteAudit.sidebarOpened({
                      note_id: noteId,
                      encounter_id: encounterId,
                      source: 'section_badge',
                      issue_count: sectionIssueCount,
                    });
                    // Open sidebar if closed, then switch to audit mode and highlight section
                    dispatch(setChatVisibility(true));
                    dispatch(openAuditForSection(section.section_id));
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.error.main,
                    color: 'white',
                    borderRadius: '10px',
                    minWidth: 20,
                    height: 20,
                    px: 0.75,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    ml: 1,
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: theme.palette.error.dark,
                    },
                  }}
                >
                  {sectionIssueCount}
                </Box>
              </Tooltip>
            )}

            {/* Compliance Rule Indicators - horizontal row next to heading */}
            {complianceRules.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '4px',
                  ml: 1,
                  flexShrink: 0,
                }}
              >
                {complianceRules.map(rule => (
                  <Box
                    key={rule.id}
                    ref={el => {
                      indicatorRefs.current[rule.id] = el as HTMLElement | null;
                    }}
                  >
                    <ComplianceRuleIndicator
                      rule={rule}
                      isHovered={hoveredRuleId === rule.id}
                      onMouseEnter={() => handleIndicatorEnter(rule.id)}
                      onMouseLeave={handleIndicatorLeave}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Copy Section Button */}
          <Button
            variant="text"
            size="small"
            onClick={handleCopySection}
            disabled={copyState === 'copying'}
            startIcon={
              copyState === 'copying' ? (
                <CircularProgress size={16} />
              ) : copyState === 'success' ? (
                <CheckIcon />
              ) : (
                <ContentCopyIcon />
              )
            }
            aria-label={`Copy section ${section.name}`}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 'auto',
              px: 1.5,
              color: copyState === 'success' ? 'success.main' : 'text.secondary',
              '&:hover': {
                color: copyState === 'success' ? 'success.main' : 'primary.main',
                bgcolor: 'action.hover',
              },
              '&.Mui-disabled': {
                color: 'text.disabled',
              },
            }}
          >
            {copyState === 'copying' ? 'Copying...' : copyState === 'success' ? 'Copied!' : 'Copy'}
          </Button>
        </Box>

        {/* Editable Content or Loading State */}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              p: 8,
              minHeight: 200,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Generating section...
            </Typography>
          </Box>
        ) : (
          <Box
            onClick={handleEditorWrapperClick}
            sx={{
              position: 'relative',
              // Always reserve space for toolbar to prevent layout shift
              pt: editable ? '36px' : 0,
              pb: editable ? 1.5 : 0,
              px: editable ? 1.5 : 0,
              mx: editable ? -1.5 : 0,
              mt: '-1px',
              borderRadius: '12px',
              // Subtle border when section is active (hovered or focused)
              border: '1px solid',
              borderColor:
                (isHovered || isFocused) && editable && !isStreaming ? '#E6E1FF' : 'transparent',
              backgroundColor:
                (isHovered || isFocused) && editable && !isStreaming
                  ? 'rgba(0, 0, 0, 0.01)'
                  : 'transparent',
              transition: 'border-color 0.15s ease, background-color 0.15s ease',
            }}
          >
            <EditorView
              markdown={displayContent}
              editable={editable && !isStreaming} // Disable in preview/completed modes, during streaming, and during approval
              onMarkdownChange={handleMarkdownChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              enableDiff={ENABLE_DIFF_OVERRIDE && isAffected && !!edit?.diff}
              diff={edit?.diff}
              sideMenu={false}
              formattingToolbar={false} // Disable default popup toolbar
              // Trouble Words System
              // Dynamically computed per-section misspellings
              enableMisspelling={sectionMisspellings.length > 0}
              misspelledWords={sectionMisspellings}
              onWordClick={handleWordClick}
              troubleWords={troubleWords}
              // Bracket Highlight Plugin
              enableBracketHighlight={true}
              onBracketClick={handleBracketClick}
              onBracketsChanged={handleBracketsChanged}
              // Editor Reference
              onEditorReady={handleEditorReady}
              // Unique Style ID
              styleId={`note-section-editor-${section.section_id}`}
              // Analytics Integration
              analytics={{
                enabled: true,
                docId: `note:${noteId}:section:${section.section_id}`,
                getDocVersion,
                metadata: {
                  section_id: section.section_id,
                  module_id: section.module_id ?? undefined,
                  note_id: noteId,
                  encounter_id: encounterId,
                },
              }}
              sx={{
                mx: '-2rem',
                // Fade out slightly during streaming to indicate temporary state
                ...(isStreaming && {
                  opacity: 0.8,
                  pointerEvents: 'none',
                }),
              }}
            >
              {/* Custom formatting toolbar that appears on hover or focus at the top of the section */}
              <SectionFormattingToolbar
                isVisible={(isHovered || isFocused) && editable && !isStreaming}
              />
            </EditorView>
          </Box>
        )}

        {/* Bracket Replacement Popover */}
        <BracketReplacementPopover
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          bracketMatch={bracketMatch}
          onClose={handleBracketPopoverClose}
          onReplace={handleBracketReplace}
        />

        {/* Attestation Edit Popover */}
        {hoveredAttestationEdit && hoveredAttestationEditIndex !== null && (
          <AttestationEditPopover
            attestationEdit={hoveredAttestationEdit}
            anchorEl={attestationAnchorEl}
            open={Boolean(hoveredAttestationEdit && attestationAnchorEl)}
            onClose={handleAttestationPopoverClose}
            onAccept={() => handleAcceptAttestationEdit(hoveredAttestationEditIndex)}
            onReject={() => handleRejectAttestationEdit(hoveredAttestationEditIndex)}
            onMouseEnter={handleAttestationPopoverMouseEnter}
            onMouseLeave={handleAttestationPopoverMouseLeave}
          />
        )}

        {/* Compliance Rule Popover */}
        {hoveredRule && (
          <ComplianceRulePopover
            rule={hoveredRule}
            anchorEl={anchorEl}
            open={Boolean(hoveredRule && anchorEl)}
            onClose={handlePopoverClose}
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handlePopoverMouseLeave}
          />
        )}

        {/* Section Diff Modal */}
        {isAffected && previousContent !== undefined && (
          <SectionDiffModal
            open={diffModalOpen}
            onClose={handleDiffClose}
            section={section}
            newContent={displayContent}
            previousContent={previousContent}
            onApprove={handleDiffApprove}
            onReject={handleDiffReject}
          />
        )}
      </Box>
    );
  },
);

NoteSectionItem.displayName = 'NoteSectionItem';
